import React, { MouseEventHandler, useState, useRef, useEffect } from "react";
import classes from "./infoTooltip.module.scss";

interface IProps {
  children: React.ReactNode;
  tooltip?: string;
  pos?: { top: number, left: number };
  classesProp?: string;
  onClick?: MouseEventHandler;
  element?: "span" | "div" | "label" | "submit";
}

const InfoTooltip = ({
  children,
  tooltip,
  pos,
  classesProp,
  onClick,
  element = "div"
}: IProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [posX, setPosX] = useState(0);
  const [posY, setPosY] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringRef = useRef(false);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const mouseEnter = tooltip
    ? (e: React.MouseEvent) => {
      isHoveringRef.current = true;
      setPosX(e.clientX);
      setPosY(e.clientY + window.scrollY);

      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set new timer
      timerRef.current = setTimeout(() => {
        if (isHoveringRef.current) {
          setIsVisible(true);
        }
      }, 1000);
    }
    : undefined;

  const throttle = (func: Function, limit: number) => {
    let inThrottle = false;
    return (...args: any[]) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  const mouseMove = tooltip
    ? throttle((e: React.MouseEvent) => {
      setPosX(e.clientX);
      setPosY(e.clientY + window.scrollY);
    }, 100) // Only runs every 100ms instead of every mouse movement
    : undefined;

  const mouseLeave = tooltip
    ? () => {
      isHoveringRef.current = false;

      // Clear timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      setIsVisible(false);
    }
    : undefined;

  const tooltipContent = (
    <>
      {children}
      {isVisible && tooltip && (
        <div className={classes.tooltip}
          style={{ top: posY + (pos?.top || 20), left: pos?.left || posX }}
        >
          {tooltip}
        </div>
      )}
    </>
  );

  const commonProps = {
    className: classesProp,
    onMouseEnter: mouseEnter,
    onMouseMove: mouseMove,
    onMouseLeave: mouseLeave,
    onClick: onClick
  };

  switch (element) {
    case "span":
      return <span {...commonProps}>{tooltipContent}</span>;
    case "label":
      return <label {...commonProps}>{tooltipContent}</label>;
    case "submit":
      return <button type="submit" {...commonProps}>{tooltipContent}</button>;
    default:
      return <div {...commonProps}>{tooltipContent}</div>;
  }
};

export default InfoTooltip;
